import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LogOut, Inbox, Briefcase, BarChart3 } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { Logo } from '../components/Logo';
import api, { getAdminToken, setAdminToken } from '../lib/api';
import { ADMIN } from '../constants/testIds';

const TABS = [
  { key: 'stats', icon: BarChart3, tid: ADMIN.tabStats },
  { key: 'contacts', icon: Inbox, tid: ADMIN.tabContacts },
  { key: 'b2b', icon: Briefcase, tid: ADMIN.tabB2B },
];

export default function AdminDashboardPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [b2b, setB2B] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasToken = Boolean(getAdminToken());

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, c, b] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/contacts'),
        api.get('/admin/b2b'),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setB2B(b.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasToken) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    setAdminToken(null);
    navigate('/admin/login');
  };

  const setContactStatus = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    refresh();
  };
  const setB2BStatus = async (id, status) => {
    await api.patch(`/admin/b2b/${id}`, { status });
    refresh();
  };

  return (
    <main className="min-h-screen bg-paper">
      <header className="bg-pure border-b border-sand sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Logo size="sm" />
          <button
            data-testid={ADMIN.logout}
            onClick={logout}
            className="flex items-center gap-2 text-sm uppercase tracking-widest text-ink hover:text-lagoon transition-colors"
          >
            <LogOut size={16} /> {t('admin.logout')}
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <h1 className="display text-5xl text-ink">{t('admin.dashboardTitle')}</h1>

        <nav className="mt-12 flex border-b border-sand">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              data-testid={tb.tid}
              onClick={() => setTab(tb.key)}
              className={`flex items-center gap-2 px-6 py-4 text-sm uppercase tracking-widest border-b-2 transition-colors ${
                tab === tb.key
                  ? 'border-ocean text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              <tb.icon size={16} />
              {t(`admin.${tb.key}Tab`)}
            </button>
          ))}
        </nav>

        <div className="mt-10">
          {loading && <p className="text-ink-muted">…</p>}
          {!loading && tab === 'stats' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <StatCard label="Messages totaux" value={stats.contacts.total} />
              <StatCard label="Messages nouveaux" value={stats.contacts.new} accent />
              <StatCard label="Demandes B2B totales" value={stats.b2b.total} />
              <StatCard label="B2B nouvelles" value={stats.b2b.new} accent />
            </div>
          )}

          {!loading && tab === 'contacts' && (
            <div data-testid={ADMIN.contactsTable}>
              {contacts.length === 0 ? (
                <p className="text-ink-muted py-12 text-center">{t('admin.noData')}</p>
              ) : (
                <div className="bg-pure border border-sand divide-y divide-sand">
                  {contacts.map((c) => (
                    <ContactCard
                      key={c.id}
                      item={c}
                      onMarkRead={() => setContactStatus(c.id, 'read')}
                      onArchive={() => setContactStatus(c.id, 'archived')}
                      onReopen={() => setContactStatus(c.id, 'new')}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && tab === 'b2b' && (
            <div data-testid={ADMIN.b2bTable}>
              {b2b.length === 0 ? (
                <p className="text-ink-muted py-12 text-center">{t('admin.noData')}</p>
              ) : (
                <div className="bg-pure border border-sand divide-y divide-sand">
                  {b2b.map((c) => (
                    <B2BCard
                      key={c.id}
                      item={c}
                      onMarkRead={() => setB2BStatus(c.id, 'read')}
                      onArchive={() => setB2BStatus(c.id, 'archived')}
                      onReopen={() => setB2BStatus(c.id, 'new')}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-pure border border-sand p-8">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif text-6xl mt-4 ${accent ? 'text-lagoon' : 'text-ink'}`}>{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    new: 'bg-lagoon/10 text-lagoon',
    read: 'bg-sand text-ink-soft',
    archived: 'bg-ink/10 text-ink-muted',
  };
  return (
    <span className={`text-[0.65rem] uppercase tracking-widest px-2 py-1 ${styles[status] || styles.new}`}>
      {status}
    </span>
  );
}

function ContactCard({ item, onMarkRead, onArchive, onReopen, t }) {
  return (
    <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-4">
        <div className="flex items-center gap-3">
          <p className="font-serif text-xl text-ink">{item.name}</p>
          <StatusBadge status={item.status} />
        </div>
        <a href={`mailto:${item.email}`} className="text-sm text-ink-soft link-underline mt-1 inline-block">
          {item.email}
        </a>
        {item.phone && <p className="text-xs text-ink-muted mt-1">{item.phone}</p>}
        <p className="text-xs text-ink-muted mt-2">{new Date(item.created_at).toLocaleString('fr-FR')}</p>
      </div>
      <div className="md:col-span-6">
        {item.subject && <p className="eyebrow text-ink">{item.subject}</p>}
        <p className="text-ink-soft text-sm mt-2 leading-relaxed whitespace-pre-wrap">{item.message}</p>
      </div>
      <div className="md:col-span-2 flex md:flex-col gap-2 justify-end text-xs">
        {item.status !== 'read' && (
          <button onClick={onMarkRead} className="link-underline text-ink">{t('admin.markRead')}</button>
        )}
        {item.status !== 'archived' && (
          <button onClick={onArchive} className="link-underline text-ink-muted">{t('admin.archive')}</button>
        )}
        {item.status !== 'new' && (
          <button onClick={onReopen} className="link-underline text-lagoon">{t('admin.reopen')}</button>
        )}
      </div>
    </div>
  );
}

function B2BCard({ item, onMarkRead, onArchive, onReopen, t }) {
  return (
    <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-4">
        <div className="flex items-center gap-3">
          <p className="font-serif text-xl text-ink">{item.company_name}</p>
          <StatusBadge status={item.status} />
        </div>
        <p className="text-sm text-ink-soft mt-1">{item.contact_name}</p>
        <a href={`mailto:${item.email}`} className="text-sm text-ink-soft link-underline mt-1 inline-block">
          {item.email}
        </a>
        {item.phone && (
          <p className="text-xs text-ink-muted mt-1">Tél. : {item.phone}</p>
        )}
        {item.tahiti_number && (
          <p className="text-xs text-ink-muted mt-1">N° TAHITI : {item.tahiti_number}</p>
        )}
        <p className="text-xs text-ink-muted mt-1">
          {item.business_type === 'distributor' ? 'Distributeur' : item.business_type === 'retailer' ? 'Revendeur' : item.business_type}
        </p>
        {item.address && (
          <p className="text-xs text-ink-muted mt-2 italic">{item.address}</p>
        )}
        {item.website && (
          <a href={item.website} target="_blank" rel="noreferrer" className="text-xs text-lagoon link-underline mt-1 inline-block">
            {item.website}
          </a>
        )}
        <p className="text-xs text-ink-muted mt-2">{new Date(item.created_at).toLocaleString('fr-FR')}</p>
      </div>
      <div className="md:col-span-6">
        {item.message ? (
          <p className="text-ink-soft text-sm leading-relaxed whitespace-pre-wrap">{item.message}</p>
        ) : (
          <p className="text-ink-muted text-sm italic">Aucun message complémentaire.</p>
        )}
      </div>
      <div className="md:col-span-2 flex md:flex-col gap-2 justify-end text-xs">
        {item.status !== 'read' && (
          <button onClick={onMarkRead} className="link-underline text-ink">{t('admin.markRead')}</button>
        )}
        {item.status !== 'archived' && (
          <button onClick={onArchive} className="link-underline text-ink-muted">{t('admin.archive')}</button>
        )}
        {item.status !== 'new' && (
          <button onClick={onReopen} className="link-underline text-lagoon">{t('admin.reopen')}</button>
        )}
      </div>
    </div>
  );
}
